/**
 * pageSetup — page size / orientation / margins for the paginated editor.
 *
 * One config object drives three consumers:
 *   - PaginationPlus (screen rendering, px @ 96dpi)
 *   - DOCX export (sectPr pgSz/pgMar, twips)
 *   - the print window (@page rule, inches)
 *
 * Persisted in document meta as `meta.pageSetup`. A4 matters for CNIPA/EPO
 * filings; Letter is the USPTO default and the editor's historical behavior.
 */

export type PageSizeId = "letter" | "a4" | "legal";
export type PageOrientation = "portrait" | "landscape";

export interface PageMargins {
  /** inches */
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface PageSetup {
  size: PageSizeId;
  orientation: PageOrientation;
  margins: PageMargins;
}

interface PageSizeDef {
  label: string;
  /** portrait dimensions in inches */
  widthIn: number;
  heightIn: number;
  /** portrait dimensions in twips (1/1440 in) for DOCX */
  widthTwips: number;
  heightTwips: number;
}

export const PAGE_SIZES: Record<PageSizeId, PageSizeDef> = {
  letter: {
    label: "Letter (8.5″ × 11″)",
    widthIn: 8.5,
    heightIn: 11,
    widthTwips: 12240,
    heightTwips: 15840,
  },
  a4: {
    label: "A4 (210mm × 297mm)",
    widthIn: 8.268,
    heightIn: 11.693,
    widthTwips: 11906,
    heightTwips: 16838,
  },
  legal: {
    label: "Legal (8.5″ × 14″)",
    widthIn: 8.5,
    heightIn: 14,
    widthTwips: 12240,
    heightTwips: 20160,
  },
};

/**
 * The editor's historical defaults (hardcoded before page setup existed):
 * Letter portrait, 0.5" top/bottom, 0.75" left/right.
 */
export const DEFAULT_PAGE_SETUP: PageSetup = {
  size: "letter",
  orientation: "portrait",
  margins: { top: 0.5, right: 0.75, bottom: 0.5, left: 0.75 },
};

export const MARGIN_PRESETS: Record<
  string,
  { label: string; margins: PageMargins }
> = {
  normal: {
    label: "Normal (1″)",
    margins: { top: 1, right: 1, bottom: 1, left: 1 },
  },
  compact: {
    label: "Compact (0.5″ / 0.75″)",
    margins: { top: 0.5, right: 0.75, bottom: 0.5, left: 0.75 },
  },
  narrow: {
    label: "Narrow (0.5″)",
    margins: { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 },
  },
  wide: {
    label: "Wide (1″ / 2″)",
    margins: { top: 1, right: 2, bottom: 1, left: 2 },
  },
};

const PX_PER_IN = 96;
const TWIPS_PER_IN = 1440;

function inToPx(inches: number): number {
  return Math.round(inches * PX_PER_IN);
}

function inToTwips(inches: number): number {
  return Math.round(inches * TWIPS_PER_IN);
}

/** Oriented page dimensions in inches. */
export function pageDimensionsIn(setup: PageSetup): {
  widthIn: number;
  heightIn: number;
} {
  const def = PAGE_SIZES[setup.size] || PAGE_SIZES.letter;
  return setup.orientation === "landscape"
    ? { widthIn: def.heightIn, heightIn: def.widthIn }
    : { widthIn: def.widthIn, heightIn: def.heightIn };
}

/**
 * PaginationPlus options slice for a setup (px @ 96dpi).
 * marginTop/Bottom are the header/footer band heights in the lib's model.
 */
export function pageSetupToPagination(setup: PageSetup): {
  pageWidth: number;
  pageHeight: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
} {
  const { widthIn, heightIn } = pageDimensionsIn(setup);
  return {
    pageWidth: inToPx(widthIn),
    pageHeight: inToPx(heightIn),
    marginTop: inToPx(setup.margins.top),
    marginBottom: inToPx(setup.margins.bottom),
    marginLeft: inToPx(setup.margins.left),
    marginRight: inToPx(setup.margins.right),
  };
}

/**
 * DOCX section page properties (twips). The docx lib expects the PORTRAIT
 * width/height plus an orientation flag and normalizes the emitted pgSz
 * itself (pre-swapping the dimensions gets un-swapped — verified against
 * docx 8.5).
 */
export function pageSetupToDocxPage(setup: PageSetup): {
  size: {
    width: number;
    height: number;
    orientation: "portrait" | "landscape";
  };
  margin: { top: number; right: number; bottom: number; left: number };
} {
  const def = PAGE_SIZES[setup.size] || PAGE_SIZES.letter;
  return {
    size: {
      width: def.widthTwips,
      height: def.heightTwips,
      orientation: setup.orientation,
    },
    margin: {
      top: inToTwips(setup.margins.top),
      right: inToTwips(setup.margins.right),
      bottom: inToTwips(setup.margins.bottom),
      left: inToTwips(setup.margins.left),
    },
  };
}

/** Stable string for React keys / change detection. */
export function pageSetupKey(setup: PageSetup): string {
  const m = setup.margins;
  return `${setup.size}-${setup.orientation}-${m.top}-${m.right}-${m.bottom}-${m.left}`;
}

/** Normalize anything persisted/imported into a full PageSetup. */
export function normalizePageSetup(raw: unknown): PageSetup {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_PAGE_SETUP };
  const obj = raw as Record<string, any>;
  const size: PageSizeId = PAGE_SIZES[obj.size as PageSizeId]
    ? obj.size
    : "letter";
  const orientation: PageOrientation =
    obj.orientation === "landscape" ? "landscape" : "portrait";
  const m = obj.margins || {};
  const num = (v: any, fallback: number) =>
    typeof v === "number" && isFinite(v) && v >= 0 && v <= 4 ? v : fallback;
  return {
    size,
    orientation,
    margins: {
      top: num(m.top, DEFAULT_PAGE_SETUP.margins.top),
      right: num(m.right, DEFAULT_PAGE_SETUP.margins.right),
      bottom: num(m.bottom, DEFAULT_PAGE_SETUP.margins.bottom),
      left: num(m.left, DEFAULT_PAGE_SETUP.margins.left),
    },
  };
}

/**
 * Parse `<w:pgSz>` / `<w:pgMar>` from a sectPr element (DOCX import).
 * Returns undefined when no pgSz is present. Unknown page sizes snap to
 * the closest known size (within ~1/4"), else default to Letter.
 */
export function parseSectPrPageSetup(
  sectPrEl: Element | null
): PageSetup | undefined {
  if (!sectPrEl) return undefined;

  const W_NS = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";
  const attr = (el: Element, name: string): string =>
    el.getAttributeNS(W_NS, name) || el.getAttribute(`w:${name}`) || "";

  let pgSz: Element | null = null;
  let pgMar: Element | null = null;
  const children = sectPrEl.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;
    const local = el.localName || el.nodeName.replace(/^w:/, "");
    if (local === "pgSz") pgSz = el;
    else if (local === "pgMar") pgMar = el;
  }
  if (!pgSz) return undefined;

  const w = parseInt(attr(pgSz, "w"), 10);
  const h = parseInt(attr(pgSz, "h"), 10);
  if (!w || !h) return undefined;

  const orientAttr = attr(pgSz, "orient");
  const orientation: PageOrientation =
    orientAttr === "landscape" || (!orientAttr && w > h)
      ? "landscape"
      : "portrait";

  // Match against portrait dimensions regardless of stored orientation
  const pw = Math.min(w, h);
  const ph = Math.max(w, h);
  const TOLERANCE = 360; // 1/4 inch in twips
  let size: PageSizeId = "letter";
  (Object.keys(PAGE_SIZES) as PageSizeId[]).forEach(id => {
    const def = PAGE_SIZES[id];
    if (
      Math.abs(def.widthTwips - pw) <= TOLERANCE &&
      Math.abs(def.heightTwips - ph) <= TOLERANCE
    ) {
      size = id;
    }
  });

  const margins: PageMargins = { ...DEFAULT_PAGE_SETUP.margins };
  if (pgMar) {
    const marginIn = (name: string, fallback: number): number => {
      const v = parseInt(attr(pgMar!, name), 10);
      if (!v || v < 0) return fallback;
      // Round to 0.05" steps for clean UI values
      return Math.round((v / TWIPS_PER_IN) * 20) / 20;
    };
    margins.top = marginIn("top", margins.top);
    margins.right = marginIn("right", margins.right);
    margins.bottom = marginIn("bottom", margins.bottom);
    margins.left = marginIn("left", margins.left);
  }

  return normalizePageSetup({ size, orientation, margins });
}
